import React, { useState, useCallback, useEffect, memo } from 'react';
import { AlertCircle } from 'lucide-react';
import { checkSlugAvailability, createService } from '../../../api/services';
import { validateSlug, generateSlug, SLUG_MIN_LENGTH } from '../../../utils/slugUtils';
import Button from '../../common/button/Button';

import styles from './styles/ServiceDetails.module.css';

const initialFormState = {
    name: '',
    slug: '',
    description: '',
    requiresAuth: false,
};

const CreateServiceForm = ({ isOpen, onClose, onServiceCreated }) => {
    const [formData, setFormData] = useState(initialFormState);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [slugAvailable, setSlugAvailable] = useState(true);
    const [slugChecking, setSlugChecking] = useState(false);
    const [slugError, setSlugError] = useState(null);
    const [suggestedSlug, setSuggestedSlug] = useState('');

    // Reset all form state
    const resetForm = useCallback(() => {
        setFormData(initialFormState);
        setError(null);
        setSlugAvailable(true);
        setSlugChecking(false);
        setSlugError(null);
        setSuggestedSlug('');
    }, []);

    useEffect(() => {
        if (isOpen) {
            resetForm();
        }
    }, [isOpen, resetForm]);

    // Handle form close with reset
    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [onClose, resetForm]);

    // Apply suggested slug to form
    const applySlugSuggestion = useCallback(() => {
        if (suggestedSlug) {
            setFormData(prev => ({ ...prev, slug: suggestedSlug }));
            setSlugError(null);
        }
    }, [suggestedSlug]);

    // Handle input changes
    const handleChange = useCallback(e => {
        const { name, value, type, checked } = e.target;
        let newValue = type === 'checkbox' ? checked : value;
        newValue = name === 'slug' ? newValue.toLowerCase().replace(/ /g, '_') : newValue;

        setFormData(prev => ({ ...prev, [name]: newValue }));

        // Reset errors when fields change
        setError(null);

        // Generate slug suggestion when name changes
        if (name === 'name') {
            const newSlug = generateSlug(value);
            setSuggestedSlug(newSlug);
        }
    }, []);

    // Check slug availability
    useEffect(() => {
        if (!formData.slug) {
            setSlugError(null);
            setSlugChecking(false);
            return;
        }

        const validation = validateSlug(formData.slug);
        if (!validation.isValid) {
            setSlugError(validation.error);
            setSlugChecking(false);
            return;
        }

        const debounceTimer = setTimeout(async () => {
            setSlugChecking(true);
            try {
                const response = await checkSlugAvailability(formData.slug);
                setSlugAvailable(response.available);

                if (!response.available) {
                    setSlugError(response.error || 'This slug is already taken');
                } else {
                    // Clear error when slug becomes available
                    setSlugError(null);
                }
            } catch (err) {
                console.error('Error checking slug:', err);
                setSlugError('Error checking slug availability');
            } finally {
                setSlugChecking(false);
            }
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [formData.slug]);

    // Handle form submission
    const handleSubmit = useCallback(async e => {
        e.preventDefault();

        // Validation
        if (!formData.name || !formData.slug || !formData.description) {
            setError('Please fill in all required fields');
            return;
        }

        const slugValidation = validateSlug(formData.slug);
        if (!slugValidation.isValid) {
            setError(slugValidation.error);
            return;
        }

        if (!slugAvailable) {
            setError('This slug is already taken. Please choose a different one.');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            await createService(formData);

            // Call the onServiceCreated callback instead of navigating
            if (onServiceCreated) {
                onServiceCreated();
            }

            // Close the form
            onClose();
        } catch (err) {
            console.error('Error creating service:', err);
            setError(err.message || 'Failed to create service. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [formData, onClose, slugAvailable, onServiceCreated]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Create New Service</h1>
            </div>

            <div className={styles.blockContainer}>
                <div className={styles.blockSection}>
                    <div className={styles.secretsHeader}>
                        <h2 className={styles.secretsTitle}>Important Information about Service Creation</h2>
                    </div>
                    <div>
                        <div>
                            <p className={styles.oauthDetail}>
                                <strong>Service Logo:</strong> You can add a logo after creating the service from the service settings.
                            </p>
                            <p className={styles.oauthDetail}>
                                <strong>Service Tools:</strong> Tools can be configured after service creation from the service settings.
                            </p>
                            <p className={styles.oauthDetail}>
                                <strong>Slug cannot be changed:</strong> After creation, the service slug cannot be modified (only through support).
                            </p>
                            <p className={styles.oauthDetail}>
                                <strong>Service deletion is restricted:</strong> Services cannot be deleted (possible only through support, under specific conditions).
                            </p>
                            <p className={styles.oauthDetail}>
                                <strong>Service management:</strong> You can enable/disable your service and its tools, as well as publish/unpublish it from the public store.
                            </p>
                            <p className={styles.oauthDetail}>
                                Once created, your service will appear in the "Owned Services" tab where you can manage it.
                            </p>
                        </div>
                    </div>
                </div>

                <div className={styles.blockSection}>
                    <div className={styles.secretsHeader}>
                        <h2 className={styles.secretsTitle}>Information about the Service</h2>
                    </div>

                    <div className={styles.blockEditor}>
                        <form onSubmit={handleSubmit}>
                            <div className={styles.inputGroup}>
                                <label htmlFor="name">Service Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter service name"
                                />
                            </div>

                            <div className={styles.inputGroup}>
                                <label htmlFor="slug">
                                    Service Slug *
                                    {slugChecking && <span style={{ marginLeft: '0.5rem' }}> (checking...)</span>}
                                    {!slugChecking && formData.slug && slugAvailable && !slugError &&
                                        <span className={styles.successText} style={{ marginLeft: '0.5rem' }}>
                                            (available)
                                        </span>
                                    }
                                </label>
                                <input
                                    type="text"
                                    id="slug"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleChange}
                                    required
                                    placeholder="unique_service_identifier"
                                    className={formData.slug && slugError ? styles.inputError : ''}
                                />
                                {slugError && (
                                    <small className={styles.errorText}>
                                        {slugError}
                                    </small>
                                )}
                                <small className={styles.secretNote}>
                                    Only lowercase letters, numbers, and underscores. Must contain at least one letter. Minimum {SLUG_MIN_LENGTH} characters.
                                </small>
                                {suggestedSlug && suggestedSlug !== formData.slug && (
                                    <small
                                        className={styles.suggestion}
                                        onClick={applySlugSuggestion}
                                    >
                                        Suggestion: {suggestedSlug} (click to use)
                                    </small>
                                )}
                            </div>

                            <div className={styles.inputGroup}>
                                <label htmlFor="description">Description *</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows={4}
                                    required
                                    placeholder="Describe what your service does"
                                />
                            </div>

                            <div className={styles.inputGroup}>
                                <label>
                                    <input
                                        type="checkbox"
                                        name="requiresAuth"
                                        className={styles.checkbox}
                                        checked={formData.requiresAuth}
                                        onChange={handleChange}
                                    />
                                    Requires OAuth Authentication
                                </label>

                                <small className={styles.secretNote}>
                                    If enabled, the service will require OAuth authentication. All OAuth settings can be configured later in the OAuth tab after service creation.
                                </small>
                            </div>

                            {error && (
                                <div className={styles.secretItemEmpty} style={{ marginBottom: '1rem', padding: '0.5rem 1rem' }}>
                                    <div className={styles.secretInfo}>
                                        <div className={styles.secretEmptyIndicator}>
                                            <AlertCircle size={14} />
                                            <span>{error}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className={styles.editorActions}>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={handleClose}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="small"
                                    disabled={loading || (formData.slug && slugError) || !slugAvailable}
                                >
                                    {loading ? 'Creating...' : 'Create Service'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(CreateServiceForm);
